import React, { Suspense, lazy } from "react";
import ChunkErrorBoundary from "components/ChunkErrorBoundary";
import {
  useRoutes,
} from "react-router-dom";

import "../styles/app.global.scss";
import "../assets/styles/css/App.css";
import Loader from "components/Loader";
import {
  authRouteConstants,
  dashboardRouteConstants,
} from "constants/routeConstants";
import ProtectedRoute from "components/ProtectedRoute";
import NotFoundRedirect from "components/NotFoundRedirect";

// ─── Lazy-loaded views (code splitting) ───────────────────────────────────────
// Each view is only downloaded when the user first navigates to that route,
// but we add a webpackPrefetch magic comment to critical paths for smoother navigation.
const Landing = lazy(() => import(/* webpackPrefetch: true */ "views/Landing"));
const Authenticate = lazy(() => import(/* webpackPrefetch: true */ "views/Authenticate"));
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard"));
const InitialSetup = lazy(() => import(/* webpackPrefetch: true */ "views/InitialSetup"));

// Dashboard pages
const Home = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Home"));
const Tutorial = lazy(() => import("views/Dashboard/Tutorial/Tutorial"));
const Help = lazy(() => import("views/Dashboard/Help/help"));

// Website
const Domains = lazy(() => import("views/Dashboard/Website/Domains"));
const DomainMappingGuide = lazy(() => import("views/Dashboard/Website/Domains/DomainMappingGuide"));
const Template = lazy(() => import("views/Dashboard/Website/Template"));
const Contact = lazy(() => import("views/Dashboard/Website/Contact"));
const About = lazy(() => import("views/Dashboard/Website/About"));
const TermsAndCondition = lazy(() => import("views/Dashboard/Website/TermsAndCondition"));
const CustomTemplate = lazy(() => import("views/Dashboard/Website/CustomTemplate"));
const PrivacyPolicy = lazy(() => import("views/Dashboard/Website/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("views/Dashboard/Website/RefundPolicy/RefundPolicy"));

// Store
const Analytics = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/Analytics"));
const Products = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/Products"));
const Categories = lazy(() => import("views/Dashboard/Store/Categories"));
const UpdateStore = lazy(() => import("views/Dashboard/Store/UpdateStore"));
const Payments = lazy(() => import("views/Dashboard/Store/Payments"));
const Delivery = lazy(() => import("views/Dashboard/Store/Delivery"));
const Reviews = lazy(() => import("views/Dashboard/Store/Reviews"));
const ShippingMethod = lazy(() => import("views/Dashboard/Store/Shipping/ShippingMethod"));
const PilotPos = lazy(() => import("views/Dashboard/Store/Pilot-Pos"));
const OrderNote = lazy(() => import("views/Dashboard/Store/Order-Note"));
const TipSettings = lazy(() => import("views/Dashboard/Store/Tip-Settings"));
const AddonGroups = lazy(() => import("views/Dashboard/Store/Addon-Groups"));
const RawMaterials = lazy(() => import("views/Dashboard/Store/RawMaterials"));
const Recipes = lazy(() => import("views/Dashboard/Store/Recipes"));

// Reports
const Orders = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/Orders"));
const SalesReport = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/SalesReport"));
const InventoryReport = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/InventoryReport"));
const SubuserReport = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/SubuserReport"));
const ExecutiveReport = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Store/ExecutiveReport"));
const Customers = lazy(() => import("views/Dashboard/Store/Customers"));

// Marketing
const Vouchers = lazy(() => import("views/Dashboard/Store/Vouchers"));
const QRCode = lazy(() => import("views/Dashboard/Store/QRCode"));

// Account
const Billing = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Account/Billing"));
const Profile = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Account/Profile"));
const SubUsers = lazy(() => import(/* webpackPrefetch: true */ "views/Dashboard/Account/SubUsers"));

// Landing pages
const ContactUsv1 = lazy(() => import(/* webpackPrefetch: true */ "views/Landing/ContactUsv1"));
const Pricingv1 = lazy(() => import(/* webpackPrefetch: true */ "views/Landing/Pricingv1"));
const Aboutv1 = lazy(() => import("views/Landing/Aboutv1"));
const Privacypolicyv1 = lazy(() => import("views/Landing/Privacypolicyv1"));
const Termasandcondistion = lazy(() => import("views/Landing/Termasandcondistion"));
const EULA = lazy(() => import("views/Landing/EULA"));
const Paymentsv1 = lazy(() => import("views/Landing/Paymentsv1"));
const Partnerv1 = lazy(() => import("views/Landing/Partnerv1"));
const Shippingv1 = lazy(() => import("views/Landing/Shippingv1"));
const FAQV1 = lazy(() => import("views/Landing/FAQV1"));
const SolutionsQRCode = lazy(() => import("views/Landing/Qrcode"));
const Productsolution = lazy(() => import("views/Landing/Productsolution"));
const SolutionTuckshop = lazy(() => import("views/Landing/Tuckshop"));
const TableOrdering = lazy(() => import("views/Landing/tableOrdering"));
const NewsUpdates = lazy(() => import("views/Landing/NewsUpdates"));
const ArticlePage = lazy(() => import("views/Landing/ArticlePage"));
const Article1 = lazy(() => import("views/Landing/Blog/Article1"));
const Article2 = lazy(() => import("views/Landing/Blog/Article2"));
const Article3 = lazy(() => import("views/Landing/Blog/Article3"));
const Article4 = lazy(() => import("views/Landing/Blog/Article4"));
const Article5 = lazy(() => import("views/Landing/Blog/Article5"));
const Article6 = lazy(() => import("views/Landing/Blog/Article6"));

// NaN (404/fallback)
const NaN = lazy(() => import("views/Dashboard/NaN"));
// ─────────────────────────────────────────────────────────────────────────────

// Shared Suspense wrapper used throughout the route tree
const SuspenseWrapper = ({ children }) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<Loader open={true} />}>{children}</Suspense>
  </ChunkErrorBoundary>
);


// eslint-disable-next-line
export default () => {
  let element = useRoutes([
    {
      path: authRouteConstants.HOME,
      element: (
        <SuspenseWrapper>
          <Landing />
        </SuspenseWrapper>
      ),
    },
    {
      path: authRouteConstants.CONATCT,
      element: <SuspenseWrapper><ContactUsv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.FAQ,
      element: <SuspenseWrapper><FAQV1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.PRICING,
      element: <SuspenseWrapper><Pricingv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ABOUT,
      element: <SuspenseWrapper><Aboutv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.PRIVACYPOLICYV1,
      element: <SuspenseWrapper><Privacypolicyv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.TERMSANDCONDISTION,
      element: <SuspenseWrapper><Termasandcondistion /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.EULA,
      element: <SuspenseWrapper><EULA /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.PAYMENTS,
      element: <SuspenseWrapper><Paymentsv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.PARTNERS,
      element: <SuspenseWrapper><Partnerv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.SHIPPINGINTEGRATION,
      element: <SuspenseWrapper><Shippingv1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.SOLUTIONQRCODE,
      element: <SuspenseWrapper><SolutionsQRCode /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.TABLEORDERING,
      element: <SuspenseWrapper><TableOrdering /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.PRODUCTS,
      element: <SuspenseWrapper><Productsolution /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.TUCKSHOP,
      element: <SuspenseWrapper><SolutionTuckshop /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.NEWS_UPDATES,
      element: <SuspenseWrapper><NewsUpdates /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.BLOG_ARTICLE,
      element: <SuspenseWrapper><ArticlePage /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_1,
      element: <SuspenseWrapper><Article1 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_2,
      element: <SuspenseWrapper><Article2 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_3,
      element: <SuspenseWrapper><Article3 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_4,
      element: <SuspenseWrapper><Article4 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_5,
      element: <SuspenseWrapper><Article5 /></SuspenseWrapper>,
    },
    {
      path: authRouteConstants.ARTICLE_6,
      element: <SuspenseWrapper><Article6 /></SuspenseWrapper>,
    },
    {
      path: `${authRouteConstants.AUTH}/:page`,
      element: <SuspenseWrapper><Authenticate /></SuspenseWrapper>,
    },
    {
      path: "/",
      element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
      children: [
        {
          path: dashboardRouteConstants.HOME,
          element: (
            <Home
              breadcrumbs={[
                {
                  path: dashboardRouteConstants.HOME,
                  name: "Home",
                },
              ]}
            />
          ),
        },
        {
          path: "/dashboard/tutorial",
          element: <Tutorial />,
        },
        {
          path: "/dashboard/help",
          element: <Help />,
        },
        // Website
        {
          path: "/website/analytics",
          element: <NaN />,
        },
        {
          path: "/website/templates",
          element: (
            <Template
              breadcrumbs={[{ path: "/website/templates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/customtemplates",
          element: (
            <CustomTemplate
              breadcrumbs={[{ path: "/website/customtemplates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/domain",
          element: (
            <Domains
              breadcrumbs={[{ path: "/website/templates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/domain/guide",
          element: (
            <DomainMappingGuide
              breadcrumbs={[{ path: "/website/domain", name: "Domains" }]}
            />
          ),
        },
        {
          path: "/website/contact",
          element: (
            <Contact
              breadcrumbs={[{ path: "/website/templates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/about",
          element: (
            <About
              breadcrumbs={[{ path: "/website/templates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/terms-conditions",
          element: (
            <TermsAndCondition
              breadcrumbs={[{ path: "/website/templates", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/returnandrefunds",
          element: (
            <PrivacyPolicy
              breadcrumbs={[{ path: "/website/returnandrefunds", name: "Website" }]}
            />
          ),
        },
        {
          path: "/website/refundpolicy",
          element: (
            <RefundPolicy
              breadcrumbs={[{ path: "/website/refundpolicy", name: "Website" }]}
            />
          ),
        },
        // Store
        {
          path: "/store/analytics",
          element: (
            <Analytics
              breadcrumbs={[{ path: "/store/analytics", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/products",
          element: (
            <Products
              breadcrumbs={[{ path: "/store/products", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/categories",
          element: (
            <Categories
              breadcrumbs={[{ path: "/store/products", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/updatestore",
          element: (
            <UpdateStore
              breadcrumbs={[{ path: "/store/products", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/integrations/pilot-pos",
          element: (
            <PilotPos
              breadcrumbs={[{ path: "/store/integrations/pilot-pos", name: "Integration" }]}
            />
          ),
        },
        {
          path: "/store/order-note",
          element: (
            <OrderNote
              breadcrumbs={[{ path: "/store/order-note", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/tip-settings",
          element: (
            <TipSettings
              breadcrumbs={[{ path: "/store/tip-settings", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/addon-groups",
          element: (
            <AddonGroups
              breadcrumbs={[{ path: "/store/addon-groups", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/raw-materials",
          element: (
            <RawMaterials
              breadcrumbs={[{ path: "/store/raw-materials", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/components-resources",
          element: (
            <Recipes
              breadcrumbs={[{ path: "/store/components-resources", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/integrations/payments",
          element: (
            <Payments
              breadcrumbs={[{ path: "/store/integrations/payments", name: "Integration" }]}
            />
          ),
        },
        {
          path: "/store/integrations/shipping",
          element: (
            <ShippingMethod
              breadcrumbs={[{ path: "/store/integrations/shipping", name: "Integration" }]}
            />
          ),
        },
        {
          path: "/store/delivery",
          element: (
            <Delivery
              breadcrumbs={[{ path: "/store/products", name: "Store" }]}
            />
          ),
        },
        {
          path: "/store/reviews",
          element: (
            <Reviews
              breadcrumbs={[{ path: "/store/products", name: "Store" }]}
            />
          ),
        },
        // Reports
        {
          path: "/reports/orders",
          element: (
            <Orders
              breadcrumbs={[{ path: "/reports/orders", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/analytics",
          element: (
            <Analytics
              breadcrumbs={[{ path: "/reports/analytics", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/salesReport",
          element: (
            <SalesReport
              breadcrumbs={[{ path: "/reports/salesReport", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/inventoryReport",
          element: (
            <InventoryReport
              breadcrumbs={[{ path: "/reports/inventoryReport", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/customers",
          element: (
            <Customers
              breadcrumbs={[{ path: "/reports/products", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/subuser-report",
          element: (
            <SubuserReport
              breadcrumbs={[{ path: "/reports/subuser-report", name: "Reports" }]}
            />
          ),
        },
        {
          path: "/reports/executive-report",
          element: (
            <ExecutiveReport
              breadcrumbs={[{ path: "/reports/executive-report", name: "Reports" }]}
            />
          ),
        },
        // Marketing
        {
          path: "/marketing/vouchers",
          element: (
            <Vouchers
              breadcrumbs={[{ path: "/marketing/vouchers", name: "Marketing" }]}
            />
          ),
        },
        {
          path: "/marketing/qrCode",
          element: (
            <QRCode
              breadcrumbs={[{ path: "/marketing/qrCode", name: "Marketing" }]}
            />
          ),
        },
        // Account
        {
          path: "/account/details",
          element: (
            <Profile
              breadcrumbs={[{ path: "/account/details", name: "Account" }]}
            />
          ),
        },
        {
          path: "/account/billing",
          element: (
            <Billing
              breadcrumbs={[{ path: "/account/details", name: "Account" }]}
            />
          ),
        },
        {
          path: "/account/subusers",
          element: (
            <SubUsers
              breadcrumbs={[{ path: "/account/subusers", name: "Account" }]}
            />
          ),
        },
        {
          path: "/account/store",
          element: <NaN />,
        },
      ],
    },
    {
      path: dashboardRouteConstants.INITIAL_SETUP,
      element: <SuspenseWrapper><InitialSetup /></SuspenseWrapper>,
    },
    {
      path: "/logout",
      element: <SuspenseWrapper><NaN /></SuspenseWrapper>,
    },
    {
      path: "*",
      element: <NotFoundRedirect />,
    },
  ]);
  return element;
};
